import {
  ZOOM_INTERVAL_LOWER_BOUND,
  ZOOM_INTERVAL_SIZE, ZOOM_INTERVAL_UPPER_BOUND
} from "./constants";

// Function to replace commas with dots and remove invalid characters
export const parseNumberInput = (input: string) =>
  // Replace commas with dots and remove non-numeric characters except for the first dot
  input
    .replace(/,/g, ".")
    .replace(/[^0-9.]/g, "")
    .replace(/(\..*)\./g, "$1");

/**
 * Function to replace the decimal point in a given number string with the decimal comma to account for different standards.
 *
 * @param {string} decimalNumber number string with a dot as separator for the decimal point.
 * @returns string number where the points have been replaced with a comma.
 */
export const decimalPointsToCommas = (decimalNumber: string) =>
  decimalNumber.replace(/\./g, ",");

/**
 * Function to localize a number to adapt the separation of decimal values to the countries standard.
 *
 * @param {string} numericalString number as a string that contains a decimal point separator.
 * @param {string} locale 2-character string representing the country, which defines how to separate decimal numbers.
 * @returns string localized version of the decimal number.
 */
export const localizeNumber = (numericalString: string, locale: string) => {
  const lowerCaseLocale = locale.toLowerCase();
  switch (lowerCaseLocale) {
    default:
    case "de":
    case "cs":
    case "cz":
    case "es":
    case "fr":
    case "it":
    case "pl":
    case "pt":
    case "nl":
      return decimalPointsToCommas(numericalString);
    case "ch":
    case "en":
      return numericalString;
  }
};

/**
 * Function to normalise the value to represent the percentile of a given range.
 *
 * @param {number} value the numerical current value that needs to get normalised.
 * @param {number} min optional the numerical minimum acceptable value.
 * @param {number} max optional the numerical maximum acceptable value.
 */
export const normalise = (
  value: number,
  min: number = 0,
  max: number = 100,
): number => ((value - min) / max) * 100;

/**
 * Function to return the numerical value between two numbers at a specified decimal midpoint.
 *
 * @param {number} x the lower bound that will get returned for a = 0.
 * @param {number} y the upper bound that will get returned for a = 1.
 * @param {number} a the decimal number that specifies the desired midpoint, ranging from 0 to 1.
 * @returns {number} the lerped number.
 */
export const lerp = (x: number, y: number, a: number): number =>
  x * (1 - a) + y * a;
/**
 * Inverse of the lerp function above.
 * Instead of passing a decimal midpoint, you can pass any value in between and the decimal equivalent value will get returned.
 *
 * @param {number} x the lower bound.
 * @param {number} y the upper bound.
 * @param {number} a the midpoint that you want to calculate the decimal of.
 * @returns {number} the decimal representation of the midpoint.
 */
export const invlerp = (x: number, y: number, a: number): number =>
  clamp((a - x) / (y - x));
/**
 * Function to clamp a number between two other numbers.
 *
 * @param {number} a the number that needs to get clamped.
 * @param {number} [min=0] the optional lower bound. Gets returned, when a < min.
 * @param {number} [max=1] the optional upper bound. Gets returned, when a > max.
 * @returns {number} the clamped number.
 */
export const clamp = (a: number, min: number = 0, max: number = 1): number =>
  Math.min(max, Math.max(min, a));
/**
 * Function to change a value from one value range to the equivalent value in another value range.
 *
 * @param {number} x1 the lower bound of the first range.
 * @param {number} y1 the upper bound of the first range.
 * @param {number} x2 the lower bound of the second range.
 * @param {number} y2 the upper bound of the second range.
 * @param {number} a the value that needs to get interpolated between the new bounds.
 * @returns {number} the new interpolated value.
 */
export const range = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  a: number,
): number => lerp(x2, y2, invlerp(x1, y1, a));

/**
 * Function to return the intervals used for debouncing the zoom data changes.
 *
 * @returns {number[]} the individual zoom interval steps that can get used as thresholds.
 */
export const getZoomIntervals = (): number[] => {
  const iVs: number[] = [];
  const range = ZOOM_INTERVAL_UPPER_BOUND - ZOOM_INTERVAL_LOWER_BOUND;
  const steps = range / ZOOM_INTERVAL_SIZE;
  for (let i = 0; i < steps; i++) {
    iVs.push(ZOOM_INTERVAL_LOWER_BOUND + i * ZOOM_INTERVAL_SIZE);
  }
  return iVs;
};
